import type { ParseKeys } from 'i18next';
import type { MiceCheck } from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { twcn } from '@web/ui/core/utils';

/** O texto de cada par (chave, estado) que o servidor emite (`checks.ts` do mice-api). */
const CHECK_TEXT: Partial<Record<`${MiceCheck['key']}.${MiceCheck['state']}`, ParseKeys>> = {
  'arrival.ok': 'mice.check.arrival.ok',
  'arrival.warn': 'mice.check.arrival.warn',
  'departure.ok': 'mice.check.departure.ok',
  'departure.warn': 'mice.check.departure.warn',
  'price.ok': 'mice.check.price.ok',
  'price.pref': 'mice.check.price.pref',
  'period.ok': 'mice.check.period.ok',
  'period.warn': 'mice.check.period.warn',
  'carrier.pref': 'mice.check.carrier.pref',
  'carrier.warn': 'mice.check.carrier.warn',
  'airport.warn': 'mice.check.airport.warn',
  'baggage.warn': 'mice.check.baggage.warn',
  'stops.pref': 'mice.check.stops.pref',
};

const ICON: Record<MiceCheck['state'], { glyph: string; tone: string }> = {
  ok: { glyph: '✓', tone: 'text-tone-green-ink' },
  warn: { glyph: '!', tone: 'text-tone-amber-ink' },
  bad: { glyph: '✗', tone: 'text-tone-red-ink' },
  pref: { glyph: '♥', tone: 'text-primary' },
};

/** Sem horário de início para o aeroporto, o servidor só compara o dia (chegada sem `limit`). */
function textKey(check: MiceCheck): ParseKeys | undefined {
  if (check.key === 'arrival' && check.values.day !== undefined)
    return 'mice.check.arrival.dayBefore';
  if (check.key === 'arrival' && check.values.limit === undefined) {
    return check.state === 'warn' ? 'mice.check.arrival.warnDay' : undefined;
  }
  return CHECK_TEXT[`${check.key}.${check.state}`];
}

/** As premissas de um voo, uma por linha, com o ícone do estado; um par sem texto não aparece. */
export function CheckList({ checks }: Readonly<{ checks: MiceCheck[] }>) {
  const items = checks.flatMap((check) => {
    const key = textKey(check);
    return key ? [{ check, text: t(key, check.values) }] : [];
  });
  if (items.length === 0) return null;
  return (
    <ul className="flex flex-col gap-0.5 text-micro text-muted-foreground">
      {items.map(({ check, text }) => (
        <li key={`${check.key}.${check.state}`} className="flex gap-1.5">
          <span
            aria-hidden
            className={twcn('w-3 flex-shrink-0 font-medium', ICON[check.state].tone)}
          >
            {ICON[check.state].glyph}
          </span>
          <span>{text}</span>
        </li>
      ))}
    </ul>
  );
}
